import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BookOpen, ArrowLeft, ExternalLink } from 'lucide-react';
import { Button } from '../../components/ui/button';
import useSmartBack from './useSmartBack';

const isMyBookieDomain = () => !window.location.hostname.match(/repti-?sitter/i);
const getHomePath = () => (isMyBookieDomain() ? '/' : '/mybookie');

// Subprocessor list. Keep this synced with the actual integrations the app uses.
// If you add a new vendor that touches customer data, add it here BEFORE you ship.
const SUBPROCESSORS = [
  {
    name: 'MongoDB Atlas',
    purpose: 'Primary database for all customer data (transactions, accounts, businesses)',
    data: 'Email, hashed password, business data, transactions, encrypted Plaid tokens',
    location: 'United States (us-east)',
    url: 'https://www.mongodb.com/cloud',
  },
  {
    name: 'Plaid',
    purpose: 'Bank account connection and transaction sync',
    data: 'Bank account metadata, transaction history (read-only)',
    location: 'United States',
    url: 'https://plaid.com/legal/',
  },
  {
    name: 'Stripe',
    purpose: 'Payment processing for subscription billing',
    data: 'Email, billing address, masked card number (PCI handled by Stripe)',
    location: 'United States · global',
    url: 'https://stripe.com/privacy',
  },
  {
    name: 'Resend',
    purpose: 'Transactional email delivery (security alerts, weekly briefs, invoices)',
    data: 'Email address, email content',
    location: 'United States',
    url: 'https://resend.com/legal/privacy-policy',
  },
  {
    name: 'OpenAI',
    purpose: 'AI insights, KPI commentary, weekly Finance Brief generation',
    data: 'Aggregated financial summaries (no raw transaction data with PII)',
    location: 'United States',
    url: 'https://openai.com/policies/privacy-policy',
  },
  {
    name: 'Anthropic',
    purpose: 'AI smart-categorization and chatbot responses',
    data: 'Transaction descriptions, user questions',
    location: 'United States',
    url: 'https://www.anthropic.com/legal/privacy',
  },
  {
    name: 'Vonage',
    purpose: 'SMS delivery for 2FA codes (when SMS 2FA is enabled)',
    data: 'Phone number, 6-digit one-time code',
    location: 'United States · global',
    url: 'https://www.vonage.com/legal/privacy-policy/',
  },
  {
    name: 'Emergent (hosting)',
    purpose: 'Application hosting and Kubernetes infrastructure',
    data: 'All data in transit and at rest',
    location: 'United States',
    url: 'https://emergent.sh',
  },
];

const Subprocessors = () => {
  const navigate = useNavigate();
  const goBack = useSmartBack(getHomePath());

  useEffect(() => {
    document.title = 'Subprocessors · MyBookie';
    const setMeta = (sel, content) => {
      const el = document.querySelector(sel);
      if (el) el.setAttribute('content', content);
    };
    setMeta('meta[name="description"]', "Vendors and third-party services that process customer data on behalf of MyBookie. Updated with every new integration.");
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${window.location.origin}/subprocessors`);
  }, []);

  return (
    <div className="min-h-screen bg-[#F6F5F2]">
      <nav className="bg-white border-b border-[#E6E8E6] sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate(getHomePath())}>
            <div className="w-10 h-10 bg-[#0A2B1D] rounded-sm flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-semibold text-[#111614] tracking-tight">MyBookie</span>
          </div>
          <Button variant="ghost" onClick={goBack} className="text-[#5C6661]" data-testid="subprocessors-back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
        </div>
      </nav>

      <section className="py-12 px-6">
        <div className="max-w-5xl mx-auto">
          <p className="text-xs uppercase tracking-[0.2em] text-[#5C6661] font-medium mb-3">Trust Center</p>
          <h1 className="text-4xl md:text-5xl font-medium text-[#111614] tracking-tight">Subprocessors</h1>
          <p className="text-lg text-[#5C6661] mt-4 max-w-2xl leading-relaxed">
            We use the following third-party vendors to deliver MyBookie. Each one processes only the
            data needed for their specific function. Subscribe to our changelog at
            {' '}<Link to="/security" className="text-[#0A2B1D] underline">/security</Link>{' '}
            to be notified when this list changes.
          </p>
          <p className="text-sm text-[#8A948F] mt-3">Last updated: May 17, 2026</p>

          <div className="mt-10 overflow-x-auto bg-white border border-[#E6E8E6] rounded-sm">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#0A2B1D] text-white">
                  <th className="text-left px-4 py-3 font-medium">Subprocessor</th>
                  <th className="text-left px-4 py-3 font-medium">Purpose</th>
                  <th className="text-left px-4 py-3 font-medium">Data Processed</th>
                  <th className="text-left px-4 py-3 font-medium">Location</th>
                </tr>
              </thead>
              <tbody>
                {SUBPROCESSORS.map((sp, idx) => (
                  <tr key={sp.name} className={idx % 2 ? 'bg-[#F6F5F2]' : 'bg-white'} data-testid={`subprocessor-${sp.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                    <td className="px-4 py-3 align-top">
                      <a href={sp.url} target="_blank" rel="noopener noreferrer" className="font-medium text-[#0A2B1D] hover:underline inline-flex items-center gap-1">
                        {sp.name} <ExternalLink className="w-3 h-3" />
                      </a>
                    </td>
                    <td className="px-4 py-3 align-top text-[#5C6661]">{sp.purpose}</td>
                    <td className="px-4 py-3 align-top text-[#5C6661]">{sp.data}</td>
                    <td className="px-4 py-3 align-top text-[#5C6661]">{sp.location}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-10 p-6 bg-white border border-[#E6E8E6] rounded-sm">
            <h2 className="text-xl font-medium text-[#111614]">How we add or remove subprocessors</h2>
            <ul className="mt-4 space-y-2 text-sm text-[#5C6661] leading-relaxed list-disc pl-5">
              <li>Every new subprocessor is reviewed for data handling, security posture, and contract terms before integration.</li>
              <li>This page is updated with every change. Material additions are announced via security@mybookie.ai to paid customers.</li>
              <li>Customers on Business+ plans may object to new subprocessors in writing within 30 days of notice; we will work with you on a resolution.</li>
              <li>For a signed DPA listing these subprocessors with EU Standard Contractual Clauses, email <a href="mailto:security@mybookie.ai" className="text-[#0A2B1D] underline">security@mybookie.ai</a>.</li>
            </ul>
          </div>
        </div>
      </section>

      <footer className="py-10 px-6 bg-white border-t border-[#E6E8E6] text-sm text-[#5C6661] mt-10">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div>© 2026 MyBookie AI LLC</div>
          <div className="flex items-center gap-4">
            <Link to="/security" className="hover:text-[#111614]">Trust Center</Link>
            <Link to="/privacy" className="hover:text-[#111614]">Privacy</Link>
            <Link to="/terms" className="hover:text-[#111614]">Terms</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Subprocessors;
